import { find } from "lodash";
import { useRouter } from "next/router";
import { useState } from "react";
import useSWRImmutable from "swr/immutable";
import { useLocations } from "../../../../common/api/useLocations";
import { fetcher } from "../../../../common/hooks";
import { Item } from "../../../../common/models";
import { FLOW_TYPES, STEPS } from "../../constants";
import { useSetUpSpacesStore } from "../../store";
import { AddSpacePurposes } from "../AddSpacePurposes";
import { AddSpacesFlowModal } from "../AddSpacesFlowModal";
import { AssignSpaces } from "../AssignSpaces";
import { LocationSetup } from "../LocationSetup";
import { SelectCompany } from "../SelectCompany";
import { SetUpAddress } from "../SetUpAddress";
import { SetUpCompany } from "../SetUpCompany";
import { SetUpLocation } from "../SetUpLocation";
import { Summary } from "../Summary";

const SetUpSpacesFlow = () => {
    const { step, isOpen, flowType } = useSetUpSpacesStore();

    const [selectedCompany, setSelectedCompany] = useState<Item>();

    const router = useRouter();
    const { companyId: companyIdFromURL, locationId } = router.query as {
        companyId: string;
        locationId: string;
    };

    const { locations } = useLocations({ companyId: companyIdFromURL });
    const existingLocation = find(locations, { id: locationId });

    const companyId = (selectedCompany?.id || companyIdFromURL) as string;

    const { data: spaceTemplates, mutate } = useSWRImmutable(
        companyId && locationId && flowType === FLOW_TYPES.ADD_SPACES
            ? `/api/space-management/v1/companies/${companyId}/locations/${locationId}/spaceTemplates`
            : null,
        fetcher
    );

    if (isOpen) {
        switch (step) {
            case STEPS.SET_COMPANY:
                return <SetUpCompany />;
            case STEPS.SELECT_COMPANY:
                return (
                    <SelectCompany
                        selectedCompany={selectedCompany}
                        onSelectCompany={setSelectedCompany}
                    />
                );
            case STEPS.SET_LOCATION:
                return <SetUpLocation />;
            case STEPS.SET_ADDRESS:
                return <SetUpAddress />;
            case STEPS.ADD_SPACES:
                return <AddSpacesFlowModal />;
            case STEPS.ADD_SPACE_PURPOSES:
                return <AddSpacePurposes spaceTemplates={spaceTemplates} />;
            case STEPS.ASSIGN_SPACES:
                return <AssignSpaces />;
            case STEPS.SUMMARY:
                return (
                    <Summary
                        existingLocation={existingLocation}
                        existingCompany={selectedCompany}
                        onAddSpaces={mutate}
                    />
                );
            case STEPS.LOCATION_SETUP:
                return <LocationSetup companyId={companyId} />;
            default:
                return null;
        }
    }

    return null;
};

export default SetUpSpacesFlow;
